/**
 * GTM dataLayer push helpers.
 *
 * The storefront's GA + ad-platform activation runs through Google Tag
 * Manager, so events reach GA4/Meta/Reddit/etc. by pushing GA4-standard
 * objects onto `window.dataLayer` (NOT via `gtag('event', …)`). GTM's
 * Custom Event triggers match on the `event` key.
 *
 * All calls are SSR-safe no-ops when `window` is unavailable.
 */

const getDataLayer = (): unknown[] | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  window.dataLayer = window.dataLayer || [];

  return window.dataLayer;
};

/**
 * Push a single dataLayer entry of the form `{ event, ...params }`.
 * Used for non-ecommerce events (e.g. `newsletter_signup`, `search`).
 */
export const pushDataLayer = (
  event: string,
  params?: Record<string, unknown>,
): void => {
  const dataLayer = getDataLayer();

  if (!dataLayer) {
    return;
  }

  dataLayer.push({ event, ...params });
};

/**
 * Push a GA4 ecommerce event. Clears the previous `ecommerce` object first
 * (GA4 best practice — stops values bleeding between events), then pushes
 * `{ event, ecommerce, ...extra }`.
 *
 * `currency` is normalised to uppercase here: GA4 requires ISO-4217 codes
 * (`GBP`), but Medusa supplies lowercase (`gbp`), which breaks GA4 revenue
 * attribution (MWP-899). Normalising at this choke point fixes every
 * ecommerce event for GA4 while leaving the PostHog/Braze payloads (and
 * their historical lowercase property values) untouched.
 *
 * @param event       GA4 event name (e.g. `add_to_cart`, `purchase`)
 * @param ecommerce   the GA4 ecommerce payload (currency, value, items, …)
 * @param extra       custom top-level params for non-standard events
 */
export const pushEcommerce = (
  event: string,
  ecommerce: Record<string, unknown>,
  extra?: Record<string, unknown>,
): void => {
  const dataLayer = getDataLayer();

  if (!dataLayer) {
    return;
  }

  const payload =
    typeof ecommerce.currency === 'string'
      ? { ...ecommerce, currency: ecommerce.currency.toUpperCase() }
      : ecommerce;

  dataLayer.push({ ecommerce: null });
  dataLayer.push({ event, ecommerce: payload, ...extra });
};
